import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { Edition } from '../../models/Edition'
import { Type } from '../../models/Type'
import { Result } from '../../types/utils'
import { Icon } from '../Icon'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { SelectList } from '../common/SelectList'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { TypePage } from './TypePage'

export type SelectableForm = {
    form: Type
    selected: boolean
}

type TypesPageProps = {
    forms: SelectableForm[]
    onSave: (forms: Type[]) => Promise<Result<any>>
    forOnboarding?: boolean
    edition?: Edition
    allowNextWithoutSelection?: boolean
    onClose: () => void
    onBack?: {
        title?: string
        onClick?: () => void
    }
}

export function TypesPage({ forms, onSave, onClose, onBack }: TypesPageProps) {
    const [isSaving, setIsSaving] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams({ mode: 'simple' })
    const { logAndDisplayError } = useErrorHandler()
    usePageTitle('Forms')

    const [selectableForms, setSelectableForms] = useState<SelectableForm[]>(forms)
    const selectableFormListItems = useMemo(() => {
        return selectableForms.map((f) => ({
            id: f.form.id ?? '',
            title: f.form.name ?? '',
            subtitle: f.form.fieldCount === 1 ? '1 question' : `${f.form.fieldCount} questions`,
            tertiaryView: (
                <Icon icon="field.large" background="round" className="text-primary-700" />
            ),
            selected: f.selected,
            button: f.selected
                ? {
                      title: 'Edit',
                      onClick: () => setSearchParams({ mode: 'simple', formID: f.form.id ?? '' }),
                  }
                : undefined,
        }))
    }, [selectableForms])
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

    const onNextHandler = useCallback(async () => {
        setIsSaving(true)
        const result = await onSave(selectableForms.filter((f) => f.selected).map((f) => f.form))
        setIsSaving(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
        }
    }, [onSave, selectableForms])

    const formToEdit = useMemo(() => {
        const formID = searchParams.get('formID')
        if (!formID) return undefined
        return selectableForms.find((f) => f.form.id === formID)?.form
    }, [searchParams, selectableForms])

    const editMode = searchParams.get('mode')

    return (
        <>
            <WorkflowPage
                onClose={onClose}
                onBack={onBack?.onClick}
                backButtonTitle={onBack?.title ?? undefined}
                footerContent={
                    <div className="flex justify-end">
                        <Button
                            intent="primary"
                            type="submit"
                            disabled={isSaving}
                            isLoading={isSaving}
                            onClick={onNextHandler}
                        >
                            Next
                        </Button>
                    </div>
                }
            >
                <Heading size="xl" className="mt-[5px]">
                    Forms
                </Heading>
                <Paragraph className="mt-4 max-w-[580px] text-center text-gray-600">
                    Attach forms to your offerings to automatically send when clients book, when
                    applicable.
                </Paragraph>

                <WorkflowPage.Card className="mt-12">
                    {selectableForms.length ? (
                        <SelectList
                            selectionType="multi"
                            items={selectableFormListItems}
                            selectedItems={selectableFormListItems.filter((f) => f.selected)}
                            onSelectItems={(items) => {
                                const selectedFormIDs = items.map((c) => c.id)
                                setSelectableForms((s) =>
                                    s.map((f) => {
                                        if (selectedFormIDs.includes(f.form.id ?? '')) {
                                            return { ...f, selected: true }
                                        }
                                        return { ...f, selected: false }
                                    })
                                )
                            }}
                        />
                    ) : (
                        <Paragraph size="md" className="text-center text-gray-600">
                            No forms found
                        </Paragraph>
                    )}
                </WorkflowPage.Card>
            </WorkflowPage>
            {editMode === 'simple' && formToEdit !== undefined && (
                <WorkflowPage.RecordLiteModal
                    isOpen
                    title={`Edit form`}
                    onClose={() => setSearchParams({ mode: 'simple' })}
                    form={formToEdit}
                    onDone={(updatedForm) => {
                        updatedForm.selectedFields = updatedForm.fields
                        updatedForm.fieldCount = updatedForm.fields.length
                        setSelectableForms((s) =>
                            s.map((c) =>
                                c.form.id === formToEdit.id ? { ...c, form: updatedForm } : c
                            )
                        )
                        setHasUnsavedChanges(true)
                    }}
                    onMoreDetails={() =>
                        setSearchParams((s) => {
                            s.set('mode', 'full')
                            return s
                        })
                    }
                />
            )}
            {editMode === 'full' && formToEdit !== undefined && (
                <TypePage
                    form={formToEdit}
                    onClose={() => setSearchParams({})}
                    onSave={(updatedForm) => {
                        setSelectableForms((s) =>
                            s.map((c) =>
                                c.form.id === formToEdit.id ? { ...c, form: updatedForm } : c
                            )
                        )
                        setSearchParams({ mode: 'simple' })
                    }}
                    allowTypeModification={false}
                />
            )}
            <NavigationBlockerPrompt
                when={hasUnsavedChanges || !isSaving}
                allowCurrentPaths={[/onboarding\/.*/]}
            />
        </>
    )
}
